import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import PlanTematicoModal from './PlanTematicoModal';

const GestionPlanesBiblia = () => {
  const API = `${window.location.origin}/api`;
  const [plans, setPlans] = useState([]);
  const [books, setBooks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showTematicoModal, setShowTematicoModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [schedulePreview, setSchedulePreview] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showParticipants, setShowParticipants] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    book_id: '',
    book_ids: [],
    duration_days: 30,
    denarios_reward: 100,
    difficulty: 'Fácil',
    tipo: 'lectura'
  });
  const [reconciling, setReconciling] = useState(false);
  const [reconcileReport, setReconcileReport] = useState(null);

  useEffect(() => {
    loadPlans();
    loadBooks();
  }, []);

  const loadBooks = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/bible/books-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBooks(response.data.books);
    } catch (error) {
      console.error('Error loading books:', error);
    }
  };

  const loadPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/bible-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Error loading plans:', error);
      toast.error('Error al cargar planes');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedBook = () => {
    return books.find(b => b.id === formData.book_id);
  };

  const calculateSchedulePreview = () => {
    if (!formData.book_ids || formData.book_ids.length === 0 || !formData.duration_days) return [];
    // Expandir todos los capítulos en orden
    const allChapters = [];
    for (const bid of formData.book_ids) {
      const b = books.find(x => x.id === bid);
      if (!b) continue;
      for (let c = 1; c <= b.chapters; c++) allChapters.push({ book: b.name, chapter: c });
    }
    const total = allChapters.length;
    if (total === 0) return [];
    const base = Math.floor(total / formData.duration_days);
    const extra = total % formData.duration_days;
    const preview = [];
    let idx = 0;
    const daysToShow = Math.min(5, formData.duration_days);
    for (let day = 1; day <= daysToShow; day++) {
      const count = base + (day <= extra ? 1 : 0);
      const slice = allChapters.slice(idx, idx + count);
      idx += count;
      if (slice.length === 0) break;
      // Agrupar por libro
      const grouped = {};
      slice.forEach(c => {
        if (!grouped[c.book]) grouped[c.book] = [];
        grouped[c.book].push(c.chapter);
      });
      const parts = Object.entries(grouped).map(([name, chs]) => {
        const s = [...chs].sort((a, b) => a - b);
        const isRange = s.every((v, i) => i === 0 || v === s[i - 1] + 1);
        if (s.length === 1) return `${name} ${s[0]}`;
        if (isRange) return `${name} ${s[0]}-${s[s.length - 1]}`;
        return `${name} ${s.join(', ')}`;
      });
      preview.push({ day, reading: parts.join('; ') });
    }
    return preview;
  };

  useEffect(() => {
    if (formData.book_ids && formData.book_ids.length > 0 && formData.duration_days) {
      setSchedulePreview(calculateSchedulePreview());
      setShowPreview(true);
    } else {
      setShowPreview(false);
    }
  }, [formData.book_ids, formData.duration_days, books]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('access_token');

      if (!formData.book_ids || formData.book_ids.length === 0) {
        toast.error('Selecciona al menos un libro');
        return;
      }

      // Enviar siempre book_ids (multi-libro) y book_id como primario por compat
      const submitData = {
        ...formData,
        book_id: formData.book_ids[0],
        book_ids: formData.book_ids
      };
      
      if (editingPlan) {
        await axios.put(
          `${API}/admin/bible-plans/${editingPlan.plan_id}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Plan actualizado exitosamente');
      } else {
        const response = await axios.post(
          `${API}/admin/bible-plans`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Plan creado exitosamente');
        console.log('Schedule preview:', response.data.schedule_preview);
      }
      
      setShowForm(false);
      setEditingPlan(null);
      setFormData({
        name: '',
        description: '',
        book_id: '',
        book_ids: [],
        duration_days: 30,
        denarios_reward: 100,
        difficulty: 'Fácil',
        tipo: 'lectura'
      });
      setShowPreview(false);
      loadPlans();
    } catch (error) {
      console.error('Error saving plan:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar plan');
    }
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description,
      book_id: plan.book_id || '',
      book_ids: (plan.book_ids && plan.book_ids.length > 0) ? plan.book_ids : (plan.book_id ? [plan.book_id] : []),
      duration_days: plan.duration_days,
      denarios_reward: plan.denarios_reward,
      difficulty: plan.difficulty,
      tipo: (plan.tipo === 'audio') ? 'audio' : 'lectura'
    });
    setShowForm(true);
  };

  const handleReconcile = async () => {
    if (!window.confirm('¿Reconciliar completaciones? Esto marcará como completados los planes que los estudiantes ya terminaron y pagará los denarios pendientes (sin duplicar pagos).')) return;
    setReconciling(true);
    setReconcileReport(null);
    try {
      const token = localStorage.getItem('access_token');
      const resp = await axios.post(
        `${API}/admin/bible-plans/reconcile-completions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReconcileReport(resp.data);
      const { fixed_completions_count, paid_denarios_count, total_denarios_paid } = resp.data;
      toast.success(`Reconciliación completa: ${fixed_completions_count} marcados como completados, ${paid_denarios_count} pagos (${total_denarios_paid} denarios)`);
      loadPlans();
    } catch (error) {
      console.error('Error reconciling plans:', error);
      toast.error(error.response?.data?.detail || 'Error al reconciliar');
    } finally {
      setReconciling(false);
    }
  };

  const handleMigrateSchedules = async () => {
    if (!window.confirm('¿Regenerar los cronogramas de planes sin schedule (legacy)? Esto crea el schedule faltante para los planes antiguos, con los libros que corresponden, sin afectar el progreso de los estudiantes.')) return;
    try {
      const token = localStorage.getItem('access_token');
      const resp = await axios.post(
        `${API}/admin/bible-plans/migrate-legacy-schedules`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { migrated_count, skipped_count, migrated } = resp.data;
      toast.success(`Schedule regenerado en ${migrated_count} planes (omitidos: ${skipped_count})`);
      console.log('Planes migrados:', migrated);
      loadPlans();
    } catch (error) {
      console.error('Error migrating schedules:', error);
      toast.error(error.response?.data?.detail || 'Error al migrar schedules');
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('¿Estás seguro de desactivar este plan?')) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/admin/bible-plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan desactivado exitosamente');
      loadPlans();
    } catch (error) {
      console.error('Error deleting plan:', error);
      toast.error('Error al desactivar plan');
    }
  };

  const handleDeletePermanent = async (planId, planName) => {
    if (!window.confirm(`⚠️ ATENCIÓN: ¿Estás seguro de ELIMINAR PERMANENTEMENTE el plan "${planName}"?\n\nEsta acción:\n- Borrará el plan de forma permanente\n- Eliminará todo el progreso de los estudiantes\n- Eliminará todas las reflexiones asociadas\n\nEsta acción NO se puede deshacer.`)) return;
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`${API}/admin/bible-plans/${planId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Plan eliminado permanentemente');
      loadPlans();
    } catch (error) {
      console.error('Error deleting plan permanently:', error);
      toast.error('Error al eliminar plan permanentemente');
    }
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'Fácil': 'bg-green-100 text-green-800',
      'Intermedio': 'bg-yellow-100 text-yellow-800',
      'Avanzado': 'bg-red-100 text-red-800'
    };
    return colors[difficulty] || 'bg-gray-100 text-gray-800';
  };

  const getTipoLabel = (tipo) => {
    const labels = { lectura: 'Lectura', audio: 'Audio' };
    return labels[tipo] || 'Lectura';
  };

  const getTipoColor = (tipo) => {
    const colors = { lectura: 'bg-blue-100 text-blue-700', audio: 'bg-purple-100 text-purple-700' };
    return colors[tipo] || 'bg-blue-100 text-blue-700';
  };

  const loadParticipants = async (planId) => {
    setLoadingParticipants(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/admin/bible-plans/${planId}/participants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParticipants(response.data.participants || []);
      setShowParticipants(planId);
    } catch (error) {
      toast.error('Error al cargar participantes');
    } finally {
      setLoadingParticipants(false);
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              📚 Gestión de Planes de Lectura Bíblica
            </h1>
            <p className="text-gray-600">
              Crea y administra planes de lectura con cronograma automático
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={handleMigrateSchedules}
              className="px-5 py-3 bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              data-testid="migrate-schedules-btn"
              title="Regenerar cronograma de planes legacy sin schedule"
            >
              <i className="fas fa-wrench mr-2" />Regenerar Schedules
            </button>
            <button
              onClick={handleReconcile}
              disabled={reconciling}
              className="px-5 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="reconcile-completions-btn"
              title="Marcar como completados los planes ya terminados y pagar denarios pendientes"
            >
              {reconciling ? (
                <><i className="fas fa-spinner fa-spin mr-2" />Reconciliando...</>
              ) : (
                <><i className="fas fa-sync-alt mr-2" />Reconciliar Completaciones</>
              )}
            </button>
            <button
              onClick={() => setShowTematicoModal(true)}
              className="px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all flex items-center gap-2"
              data-testid="nuevo-plan-tematico-btn"
              title="Generar plan temático con AI"
            >
              <i className="fas fa-sparkles" /> Plan Temático AI
            </button>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              data-testid="nuevo-plan-btn"
            >
              + Nuevo Plan
            </button>
          </div>
        </div>

        {/* Reporte de Reconciliación */}
        {reconcileReport && (
          <div className="mb-6 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-5" data-testid="reconcile-report">
            <div className="flex items-start justify-between mb-3">
              <h3 className="text-lg font-bold text-emerald-800 flex items-center gap-2">
                <i className="fas fa-check-circle" />
                Resultado de la Reconciliación
              </h3>
              <button
                onClick={() => setReconcileReport(null)}
                className="text-gray-400 hover:text-gray-700"
              ><i className="fas fa-times" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <p className="text-xs text-gray-500">Completaciones marcadas</p>
                <p className="text-2xl font-bold text-emerald-700">{reconcileReport.fixed_completions_count || 0}</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-emerald-100">
                <p className="text-xs text-gray-500">Pagos de denarios</p>
                <p className="text-2xl font-bold text-emerald-700">{reconcileReport.paid_denarios_count || 0}</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-amber-100">
                <p className="text-xs text-gray-500">Total denarios pagados</p>
                <p className="text-2xl font-bold text-amber-600">🪙 {reconcileReport.total_denarios_paid || 0}</p>
              </div>
            </div>
            {(reconcileReport.paid_denarios || []).length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer font-semibold text-emerald-700 hover:text-emerald-900">
                  Ver detalle de pagos ({reconcileReport.paid_denarios.length})
                </summary>
                <div className="mt-2 max-h-60 overflow-y-auto bg-white rounded-lg p-2 border border-emerald-100 space-y-1">
                  {reconcileReport.paid_denarios.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-100 last:border-0">
                      <span className="font-medium text-gray-700">{p.nombre || p.email}</span>
                      <span className="text-gray-500 truncate max-w-[180px]">{p.plan_name}</span>
                      <span className="font-bold text-amber-600">+{p.denarios}🪙</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Formulario */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8">
              <h2 className="text-2xl font-bold mb-6">
                {editingPlan ? 'Editar Plan' : 'Crear Nuevo Plan'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Nombre del Plan */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Nombre del Plan *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none"
                    placeholder="Ej: Plan de Lectura de Génesis"
                  />
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Descripción *
                  </label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none"
                    rows="3"
                    placeholder="Describe el objetivo del plan de lectura"
                  />
                </div>

                {/* Selector de Libros (multi-selección) */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Libros Bíblicos * (selección múltiple)
                  </label>
                  <div className="border-2 border-gray-300 rounded-lg max-h-48 overflow-y-auto p-2">
                    {formData.book_ids.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2 pb-2 border-b border-gray-200">
                        {formData.book_ids.map(bid => {
                          const book = books.find(b => b.id === bid);
                          return book ? (
                            <span key={bid} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                              {book.name}
                              <button type="button" onClick={() => setFormData({...formData, book_ids: formData.book_ids.filter(id => id !== bid)})} className="hover:text-red-500" data-testid={`remove-book-${bid}`}>
                                <i className="fas fa-times text-[10px]" />
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}
                    <p className="text-xs text-gray-500 mb-2 font-medium">Antiguo Testamento</p>
                    {books.filter(b => b.testament === 'AT').map(book => (
                      <label key={book.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={formData.book_ids.includes(book.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, book_ids: [...formData.book_ids, book.id]});
                            } else {
                              setFormData({...formData, book_ids: formData.book_ids.filter(id => id !== book.id)});
                            }
                          }}
                          className="rounded border-gray-300"
                          data-testid={`checkbox-book-${book.id}`}
                        />
                        {book.name} ({book.chapters} cap.)
                      </label>
                    ))}
                    <p className="text-xs text-gray-500 mb-2 mt-3 font-medium">Nuevo Testamento</p>
                    {books.filter(b => b.testament === 'NT').map(book => (
                      <label key={book.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={formData.book_ids.includes(book.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({...formData, book_ids: [...formData.book_ids, book.id]});
                            } else {
                              setFormData({...formData, book_ids: formData.book_ids.filter(id => id !== book.id)});
                            }
                          }}
                          className="rounded border-gray-300"
                          data-testid={`checkbox-book-${book.id}`}
                        />
                        {book.name} ({book.chapters} cap.)
                      </label>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.book_ids.length} libro(s) seleccionado(s) · {formData.book_ids.reduce((acc, bid) => {
                      const b = books.find(x => x.id === bid);
                      return acc + (b ? b.chapters : 0);
                    }, 0)} capítulos totales
                  </p>
                </div>

                {/* Duración en Días */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Duración (días) *
                  </label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.duration_days}
                    onChange={(e) => setFormData({...formData, duration_days: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none"
                  />
                  {formData.book_ids.length > 0 && formData.duration_days > 0 && (() => {
                    const totalCh = formData.book_ids.reduce((acc, bid) => {
                      const b = books.find(x => x.id === bid);
                      return acc + (b ? b.chapters : 0);
                    }, 0);
                    return (
                      <p className="mt-2 text-sm text-indigo-600 font-semibold">
                        📊 ~{(totalCh / formData.duration_days).toFixed(1)} capítulos por día
                      </p>
                    );
                  })()}
                </div>

                {/* Preview del Cronograma */}
                {showPreview && schedulePreview.length > 0 && (
                  <div className="bg-indigo-50 border-2 border-indigo-200 rounded-xl p-4">
                    <h3 className="font-semibold text-indigo-900 mb-3">
                      📅 Vista Previa del Cronograma (primeros 5 días):
                    </h3>
                    <div className="space-y-2">
                      {schedulePreview.map(day => (
                        <div key={day.day} className="flex items-center text-sm">
                          <span className="font-semibold text-indigo-600 w-16">Día {day.day}:</span>
                          <span className="text-gray-700">{day.reading}</span>
                        </div>
                      ))}
                      {formData.duration_days > 5 && (
                        <p className="text-xs text-gray-500 mt-2">
                          ... y {formData.duration_days - 5} días más
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Dificultad */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Dificultad *
                  </label>
                  <select
                    required
                    value={formData.difficulty}
                    onChange={(e) => setFormData({...formData, difficulty: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none"
                  >
                    <option value="Fácil">Fácil</option>
                    <option value="Intermedio">Intermedio</option>
                    <option value="Avanzado">Avanzado</option>
                  </select>
                </div>

                {/* Tipo de Plan */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Tipo de Plan *
                  </label>
                  <select
                    required
                    value={formData.tipo}
                    onChange={(e) => setFormData({...formData, tipo: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none"
                    data-testid="select-tipo-plan"
                  >
                    <option value="lectura">Plan de Lectura</option>
                    <option value="audio">Plan de Escuchar (Audio)</option>
                  </select>
                </div>

                {/* Recompensa de Denarios */}
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Recompensa (Denarios) *
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={formData.denarios_reward}
                    onChange={(e) => setFormData({...formData, denarios_reward: parseInt(e.target.value)})}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-indigo-600 focus:outline-none"
                  />
                  <p className="mt-2 text-sm text-gray-600">
                    🪙 Los estudiantes recibirán estos denarios al completar el plan
                  </p>
                </div>

                {/* Botones */}
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingPlan(null);
                      setShowPreview(false);
                      setFormData({
                        name: '',
                        description: '',
                        book_id: '',
                        book_ids: [],
                        duration_days: 30,
                        denarios_reward: 100,
                        difficulty: 'Fácil',
                        tipo: 'lectura'
                      });
                    }}
                    className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
                  >
                    {editingPlan ? 'Actualizar Plan' : 'Crear Plan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Lista de Planes */}
        {loading ? (
          <div className="text-center py-12">
            <div className="text-xl text-gray-600">Cargando planes...</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map(plan => (
              <div
                key={plan.plan_id}
                className={`border-2 rounded-xl p-6 ${
                  plan.active ? 'border-gray-200' : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-bold text-gray-800 pr-2">
                    {plan.name}
                  </h3>
                  <div className="flex gap-1 flex-shrink-0">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getTipoColor(plan.tipo)}`}>
                      {getTipoLabel(plan.tipo)}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getDifficultyColor(plan.difficulty)}`}>
                      {plan.difficulty}
                    </span>
                  </div>
                </div>

                <p className="text-gray-600 mb-4 text-sm">{plan.description}</p>

                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">📖 Libro:</span>
                    <span className="font-semibold">{plan.book_name || plan.category}</span>
                  </div>
                  {plan.total_chapters && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">📄 Capítulos:</span>
                      <span className="font-semibold">{plan.total_chapters}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">⏱️ Duración:</span>
                    <span className="font-semibold">{plan.duration_days} días</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">🪙 Denarios:</span>
                    <span className="font-semibold text-indigo-600">{plan.denarios_reward}</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">👥 Iniciados:</span>
                    <span className="font-semibold">{plan.total_started || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">✅ Completados:</span>
                    <span className="font-semibold text-green-600">{plan.total_completions || 0}</span>
                  </div>
                  {(plan.total_started > 0 || plan.total_completions > 0) && (
                    <button
                      onClick={() => loadParticipants(plan.plan_id)}
                      className="w-full mt-2 px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-xs font-semibold hover:bg-indigo-100 transition-all"
                    >
                      <i className="fas fa-users mr-1" />Ver Participantes
                    </button>
                  )}
                </div>

                {/* Participants panel */}
                {showParticipants === plan.plan_id && (
                  <div className="bg-white border-2 border-indigo-200 rounded-lg p-3 mb-4 max-h-48 overflow-y-auto">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-bold text-indigo-700">Participantes</span>
                      <button onClick={() => setShowParticipants(null)} className="text-gray-400 hover:text-gray-600 text-xs">
                        <i className="fas fa-times" />
                      </button>
                    </div>
                    {loadingParticipants ? (
                      <p className="text-xs text-gray-500 text-center py-2"><i className="fas fa-spinner fa-spin mr-1" />Cargando...</p>
                    ) : participants.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-2">Sin participantes</p>
                    ) : (
                      <div className="space-y-1.5">
                        {participants.map(p => (
                          <div key={p.user_id} className="flex items-center justify-between text-xs">
                            <div className="flex items-center gap-2">
                              {p.foto_perfil ? (
                                <img src={p.foto_perfil} alt="" className="w-6 h-6 rounded-full object-cover" />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-white text-[10px] font-bold">
                                  {(p.nombre || '?')[0]}
                                </div>
                              )}
                              <span className="text-gray-700 truncate max-w-[120px]">{p.nombre}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-gray-400">{p.days_done}d</span>
                              {p.completed ? (
                                <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-[10px] font-bold">Completado</span>
                              ) : (
                                <span className="bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded text-[10px] font-bold">En curso</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(plan)}
                    className="flex-1 px-4 py-2 bg-indigo-100 text-indigo-600 rounded-lg font-semibold hover:bg-indigo-200 transition-all"
                  >
                    Editar
                  </button>
                  {plan.active ? (
                    <button
                      onClick={() => handleDelete(plan.plan_id)}
                      className="flex-1 px-4 py-2 bg-yellow-100 text-yellow-700 rounded-lg font-semibold hover:bg-yellow-200 transition-all"
                    >
                      Desactivar
                    </button>
                  ) : (
                    <button
                      onClick={() => handleDeletePermanent(plan.plan_id, plan.name)}
                      className="flex-1 px-4 py-2 bg-red-100 text-red-600 rounded-lg font-semibold hover:bg-red-200 transition-all"
                    >
                      Borrar
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <PlanTematicoModal
        open={showTematicoModal}
        onClose={() => setShowTematicoModal(false)}
        onSaved={() => { loadPlans(); }}
      />
    </div>
  );
};

export default GestionPlanesBiblia;
